import { apiClient } from '@/sdk/shared/apiClient';
import { isJwtExpired } from '@/sdk/shared/auth/utils';
import type {
  AdminLoginRequest,
  AdminLoginResponse,
  AdminRegisterRequest,
  AdminRegisterResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
} from './types';

// Cookie helper functions
const getCookie = (name: string): string | undefined => {
  if (typeof document === 'undefined') return undefined;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift();
  return undefined;
};

const setCookie = (name: string, value: string, days: number = 7): void => {
  if (typeof document === 'undefined') return;
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict`;
};

const deleteCookie = (name: string): void => {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;SameSite=Strict`;
};

export const adminAuthApi = {
  login: async (data: AdminLoginRequest): Promise<AdminLoginResponse> => {
    const response = await apiClient.post<AdminLoginResponse>('/auth/admin/login', data);
    const role = response.user?.role;
    apiClient.setToken(response.token, role || 'admin');

    // Store user data in cookies for AuthContext
    if (response.user) {
      setCookie('auth_user', encodeURIComponent(JSON.stringify(response.user)));
      setCookie('auth_user_type', 'user');
    }

    return response;
  },

  register: async (data: AdminRegisterRequest): Promise<AdminRegisterResponse> => {
    const response = await apiClient.post<AdminRegisterResponse>('/auth/admin/register', data);
    apiClient.setToken(response.token, response.user.role);

    // Store user data in cookies for AuthContext
    setCookie('auth_user', encodeURIComponent(JSON.stringify(response.user)));
    setCookie('auth_user_type', 'user');

    return response;
  },

  forgotPassword: async (data: ForgotPasswordRequest): Promise<ForgotPasswordResponse> => {
    return await apiClient.post<ForgotPasswordResponse>('/auth/forgot-password', data);
  },

  resetPassword: async (data: ResetPasswordRequest): Promise<ResetPasswordResponse> => {
    return await apiClient.post<ResetPasswordResponse>('/auth/reset-password', data);
  },

  logout: (): void => {
    apiClient.clearToken();
    deleteCookie('auth_user');
    deleteCookie('auth_user_type');
  },

  getToken: (): string | undefined => {
    return apiClient.getToken();
  },

  isAuthenticated: (): boolean => {
    const token = apiClient.getToken();
    return !!token && !isJwtExpired(token);
  },

  getCurrentUser: () => {
    const userStr = getCookie('auth_user');
    const userType = getCookie('auth_user_type');
    if (userStr && userType) {
      try {
        return {
          user: JSON.parse(decodeURIComponent(userStr)),
          userType: userType as 'user',
        };
      } catch (error) {
        return null;
      }
    }
    return null;
  },
};

export default adminAuthApi;
